import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";

/**
 * Auth, users, and roles API base.
 * Dev: REACT_APP_API_URL_LOGIN_AND_LOGOUT_DEV
 * Prod: REACT_APP_API_URL_LOGIN_AND_LOGOUT (fallback when _DEV is unset)
 */
export function getAuthDevApiBaseUrl() {
  const dev = (process.env.REACT_APP_API_URL_LOGIN_AND_LOGOUT_DEV || "")
    .trim()
    .replace(/\/$/, "");
  const prod = (process.env.REACT_APP_API_URL_LOGIN_AND_LOGOUT || "")
    .trim()
    .replace(/\/$/, "");

  if (process.env.NODE_ENV === "development") {
    return dev || prod;
  }
  return prod || dev;
}

/**
 * In dev, use relative `/api/...` so CRA `package.json` proxy forwards to Node
 * (avoids CORS: localhost:3000 → 127.0.0.1:5000 is a different origin).
 */
function shouldUseDevProxy(base) {
  if (process.env.NODE_ENV !== "development") return false;
  if (!base) return true;
  if (typeof window === "undefined" || !window.location?.origin) return false;
  try {
    const apiOrigin = new URL(base).origin;
    const pageOrigin = window.location.origin;
    if (apiOrigin === pageOrigin) return false;
    const isLocalHost = (origin) => {
      const h = new URL(origin).hostname;
      return h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0";
    };
    return isLocalHost(apiOrigin) && isLocalHost(pageOrigin);
  } catch {
    return false;
  }
}

function buildUrl(path) {
  const base = getAuthDevApiBaseUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  if (shouldUseDevProxy(base)) {
    return p;
  }
  return base ? `${base}${p}` : p;
}

function parseJsonError(data) {
  if (data && typeof data === "object") {
    const o = data;
    const nested =
      o.error && typeof o.error === "object" && !Array.isArray(o.error)
        ? o.error
        : null;
    const message =
      (nested && typeof nested.message === "string" && nested.message) ||
      (typeof o.message === "string" && o.message) ||
      (Array.isArray(o.errors) &&
        o.errors[0] &&
        typeof o.errors[0].message === "string" &&
        o.errors[0].message) ||
      (typeof o.detail === "string" && o.detail) ||
      (typeof o.error === "string" && o.error) ||
      "Request failed";
    const code =
      (nested && typeof nested.code === "string" && nested.code) ||
      (typeof o.code === "string" && o.code) ||
      (typeof o.error === "string" && o.error) ||
      "REQUEST_FAILED";
    return { message, code };
  }
  return { message: "Request failed", code: "REQUEST_FAILED" };
}

function throwIfEnvelopeError(data, status) {
  if (!data || typeof data !== "object") return;
  if (data.success === false) {
    const { message, code } = parseJsonError(data);
    throw new ApiRequestError(message, code, status);
  }
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const { message, code } = parseJsonError(data);
    throw new ApiRequestError(message, code, status);
  }
}

export class ApiRequestError extends Error {
  constructor(message, code = "REQUEST_FAILED", status = 0) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code;
    this.status = status;
  }
}

export async function apiGet(path) {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
    },
  });
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (data && typeof data === "object" && data.loggedIn === false) {
      tokenManager.clearAuth();
      window.location.href = "/login";
    }
    throw new ApiRequestError(message, code, res.status);
  }
  throwIfEnvelopeError(data, res.status);
  return data;
}

export async function apiSend(path, method, body) {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  const headers = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...userTimezoneHeaders(),
  };
  const hasBody = body !== undefined && method !== "GET";
  if (hasBody) headers["Content-Type"] = "application/json";
  const res = await fetch(url, {
    method,
    headers,
    body: hasBody ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (data && typeof data === "object" && data.loggedIn === false) {
      tokenManager.clearAuth();
      window.location.href = "/login";
    }
    throw new ApiRequestError(message, code, res.status);
  }
  throwIfEnvelopeError(data, res.status);
  if (method === "DELETE" && (data === null || data === "")) {
    return { deleted: true };
  }
  return data;
}

export async function apiGetRaw(path, init) {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  return fetch(url, {
    ...init,
    method: "GET",
    headers: {
      Accept: "*/*",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
      ...(init?.headers && typeof init.headers === "object"
        ? init.headers
        : {}),
    },
  });
}
